import classnames from "classnames";
import { StickyHeaderContent } from "components/Layout";
import { DynamicPagingList, PagingInfo, PagingList } from "components/PagingList";
import { LoadingSpinner } from "@xapp/components";
import React, { PureComponent } from "react";
import grid from "styles/grid.scss";
import { MasterDetailSearch, MasterDetailSearchProps } from "./MasterDetailSearch";
import style from "./style.scss";
import { itemsToPages, setScrollPosition } from "./utils";

const DEFAULT_ITEMS_PER_PAGE = 10;

export interface MasterDetailViewProps<T> {
    currentPage?: number;
    dynamicPaging?: boolean;
    isLoading: boolean;
    items: readonly T[];
    itemsPerPage?: number;
    loadingActions?: string[];

    selectedItem?: T;
    searchViewProps?: MasterDetailSearchProps;
    pagination?: boolean;
    persistScrollPosition?: string;
    title?: string;
    totalPages?: number;
    onPageChange?(newIndex: number): void;
    onRenderDetail(item?: T): JSX.Element;
    onRenderHeader?(): JSX.Element;
    onRenderItem(item: T): JSX.Element;
    onSearch?(): void;
    onSelectItem?(): void; // Not used
}
interface MasterDetailViewState<T> {
    currentPage: number;
    isLoading: boolean;
    pages: readonly any[];
    selectedItem: T;
}

/**
 * @deprecated In favor of MasterDetail
 */
export class MasterDetailView<T> extends PureComponent<MasterDetailViewProps<T>, MasterDetailViewState<T>> {
    constructor(props: MasterDetailViewProps<T>) {
        super(props);

        this.state = {
            currentPage: props.currentPage || 1,
            isLoading: props.isLoading,
            pages: this.convertItemsToPages(
                props.items,
                props.itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
                props.pagination
            ),
            selectedItem: props.selectedItem
        };

        this.handlePageChange = this.handlePageChange.bind(this);
        this.renderMasterHeader = this.renderMasterHeader.bind(this);
    }

    componentWillReceiveProps(newProps: MasterDetailViewProps<T>) {
        this.setState({
            isLoading: newProps.isLoading,
            pages: this.convertItemsToPages(
                newProps.items,
                newProps.itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
                newProps.pagination
            ),
            selectedItem: newProps.selectedItem
        });
    }

    // todo: why different types?
    convertItemsToPages(items: readonly T[], itemsPerPage: number, pagination: boolean): readonly (readonly T[])[] | readonly T[] {
        if (!pagination) {
            return items;
        }

        return itemsToPages(items, itemsPerPage);
    }

    handlePageChange(newPage: number) {
        const { onPageChange, persistScrollPosition } = this.props;

        this.setState({ currentPage: newPage }, () => {
            if (persistScrollPosition) {
                setScrollPosition(persistScrollPosition, 0);
            }

            if (onPageChange) {
                onPageChange(newPage);
            }
        });
    }

    handleRenderHeader() {
        const { onRenderHeader } = this.props;

        if (onRenderHeader) {
            return <header className={classnames(grid.itemnogrow, style.padded)}>{onRenderHeader()}</header>;
        }
    }

    renderPagingList() {
        const {
            items,
            itemsPerPage,
            onRenderItem,
            pagination,
            persistScrollPosition,
        } = this.props;
        const { currentPage, pages } = this.state;

        const pageSize = itemsPerPage || DEFAULT_ITEMS_PER_PAGE;

        if (this.props.dynamicPaging) {
            const data: PagingInfo<T> = {
                items,
                page: currentPage,
                pageSize,
                total: this.props.totalPages * pageSize, // is not accurate
            };
            return (
                <DynamicPagingList
                    data={data}
                    activeItem={this.props.selectedItem}
                    renderItem={onRenderItem}
                    onPageChange={this.handlePageChange}
                />
            );
        } else {
            return (
                <PagingList
                    items={pagination ? pages[currentPage - 1] : pages}
                    itemsPerPage={itemsPerPage || DEFAULT_ITEMS_PER_PAGE}
                    activeItem={this.props.selectedItem}
                    onRenderItem={onRenderItem}
                    onPageChange={this.handlePageChange}
                    page={currentPage}
                    pagination={pagination}
                    persistScrollPosition={persistScrollPosition}
                />
            );
        }
    }

    renderSearchField() {
        const { searchViewProps } = this.props;

        if (searchViewProps) {
            return (
                <div className={classnames(grid.itemnogrow, style.padded)}>
                    <MasterDetailSearch {...searchViewProps} />
                </div>
            );
        }
    }

    renderMasterHeader() {
        return (<>
            {this.handleRenderHeader()}
            {this.renderSearchField()}
        </>);
    }

    render() {
        const {
            onRenderDetail,
            selectedItem
        } = this.props;
        const { isLoading } = this.state;

        return (
            <div className={style.wrapper}>
                <div className={style.left}>
                    <StickyHeaderContent
                        header={this.renderMasterHeader}
                    >
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : this.renderPagingList()}
                    </StickyHeaderContent>
                </div>

                <div className={style.right}>
                    <div className={style.viewWrap}>
                        <div className={style.detailContent}>
                            {onRenderDetail(selectedItem)}
                        </div>
                        {/* <SiteFooter /> */}
                    </div>
                </div>
            </div>
        );
    }
}
